import { useEffect, useRef } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const COUNT = 8000;

function samplePolylines(lines, count) {
  const lengths = lines.map(([x1, y1, x2, y2]) => Math.hypot(x2 - x1, y2 - y1));
  const total = lengths.reduce((a, b) => a + b, 0);
  const pts = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    let d = Math.random() * total;
    let li = 0;
    while (d > lengths[li] && li < lines.length - 1) { d -= lengths[li]; li++; }
    const [x1, y1, x2, y2] = lines[li];
    const t = lengths[li] === 0 ? 0 : d / lengths[li];
    pts[i * 3] = x1 + (x2 - x1) * t + (Math.random() - 0.5) * 0.08;
    pts[i * 3 + 1] = y1 + (y2 - y1) * t + (Math.random() - 0.5) * 0.08;
    pts[i * 3 + 2] = (Math.random() - 0.5) * 0.6;
  }
  return pts;
}

function circleLines(cx, cy, r, segs = 48) {
  const lines = [];
  for (let i = 0; i < segs; i++) {
    const a1 = (i / segs) * Math.PI * 2;
    const a2 = ((i + 1) / segs) * Math.PI * 2;
    lines.push([cx + Math.cos(a1) * r, cy + Math.sin(a1) * r, cx + Math.cos(a2) * r, cy + Math.sin(a2) * r]);
  }
  return lines;
}

function keyShape() {
  const s = 1.15;
  const lines = [
    ...circleLines(0, 1.7 * s, 1.05 * s),
    ...circleLines(0, 1.7 * s, 0.45 * s),
    [-0.16 * s, 0.7 * s, -0.16 * s, -2.3 * s],
    [0.16 * s, 0.7 * s, 0.16 * s, -2.3 * s],
    [-0.16 * s, -2.3 * s, 0.16 * s, -2.3 * s],
    [0.16 * s, -1.3 * s, 0.85 * s, -1.3 * s],
    [0.85 * s, -1.3 * s, 0.85 * s, -1.55 * s],
    [0.85 * s, -1.55 * s, 0.16 * s, -1.55 * s],
    [0.16 * s, -1.9 * s, 1.05 * s, -1.9 * s],
    [1.05 * s, -1.9 * s, 1.05 * s, -2.15 * s],
    [1.05 * s, -2.15 * s, 0.16 * s, -2.15 * s],
  ];
  return samplePolylines(lines, COUNT);
}

function houseShape() {
  const s = 1.5;
  const lines = [
    [-1.7 * s, -1.5 * s, -1.7 * s, 0.6 * s],
    [1.7 * s, -1.5 * s, 1.7 * s, 0.6 * s],
    [-1.7 * s, -1.5 * s, 1.7 * s, -1.5 * s],
    [-2.05 * s, 0.6 * s, 0, 2.1 * s],
    [2.05 * s, 0.6 * s, 0, 2.1 * s],
    [-2.05 * s, 0.6 * s, 2.05 * s, 0.6 * s],
    [-0.45 * s, -1.5 * s, -0.45 * s, -0.3 * s],
    [0.45 * s, -1.5 * s, 0.45 * s, -0.3 * s],
    [-0.45 * s, -0.3 * s, 0.45 * s, -0.3 * s],
    [1.0 * s, 1.05 * s, 1.0 * s, 1.9 * s],
    [1.35 * s, 1.05 * s, 1.35 * s, 1.65 * s],
    [1.0 * s, 1.9 * s, 1.35 * s, 1.9 * s],
  ];
  return samplePolylines(lines, COUNT);
}

const VERT = `
attribute vec3 keyPos;
attribute vec3 housePos;
attribute vec3 scatterPos;
attribute vec3 explodePos;
attribute float aSize;
attribute vec3 aColor;
attribute float aPhase;
attribute float aSpeed;
uniform float uAssembly;
uniform float uScroll;
uniform float uTime;
varying vec3 vColor;
void main() {
  vec3 base = mix(scatterPos, keyPos, uAssembly);
  float t1 = smoothstep(0.0, 0.35, uScroll);
  float t2 = smoothstep(0.35, 0.70, uScroll);
  vec3 p = mix(base, explodePos, t1);
  p = mix(p, housePos, t2);
  p += vec3(
    sin(uTime * aSpeed + aPhase),
    cos(uTime * aSpeed * 0.83 + aPhase * 1.7),
    sin(uTime * aSpeed * 0.61 + aPhase * 2.3)
  ) * 0.055;
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  gl_PointSize = aSize * (12.5 / -mv.z);
  vColor = aColor;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = `
varying vec3 vColor;
void main() {
  float d = length(gl_PointCoord - vec2(0.5));
  if (d > 0.5) discard;
  float a = smoothstep(0.5, 0.1, d);
  gl_FragColor = vec4(vColor, a * 0.9);
}`;

export const ParticleCanvas = ({ className }) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    camera.position.z = 8.5;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);

    const geo = new THREE.BufferGeometry();
    const key = keyShape();
    const house = houseShape();
    const scatter = new Float32Array(COUNT * 3);
    const explode = new Float32Array(COUNT * 3);
    const sizes = new Float32Array(COUNT);
    const colors = new Float32Array(COUNT * 3);
    const phases = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);

    const base = new THREE.Color("#5D8FC4");
    for (let i = 0; i < COUNT; i++) {
      scatter[i * 3] = (Math.random() - 0.5) * 16;
      scatter[i * 3 + 1] = (Math.random() - 0.5) * 16;
      scatter[i * 3 + 2] = (Math.random() - 0.5) * 10;
      const r = 7 + Math.random() * 9;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      explode[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      explode[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      explode[i * 3 + 2] = r * Math.cos(phi) * 0.6;
      sizes[i] = 1.2 + Math.random() * 1.6;
      const b = 0.8 + Math.random() * 0.4;
      colors[i * 3] = base.r * b;
      colors[i * 3 + 1] = base.g * b;
      colors[i * 3 + 2] = base.b * b;
      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.5 + Math.random() * 1.4;
    }

    geo.setAttribute("position", new THREE.BufferAttribute(key, 3));
    geo.setAttribute("keyPos", new THREE.BufferAttribute(key, 3));
    geo.setAttribute("housePos", new THREE.BufferAttribute(house, 3));
    geo.setAttribute("scatterPos", new THREE.BufferAttribute(scatter, 3));
    geo.setAttribute("explodePos", new THREE.BufferAttribute(explode, 3));
    geo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));
    geo.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));
    geo.setAttribute("aSpeed", new THREE.BufferAttribute(speeds, 1));

    const uniforms = {
      uAssembly: { value: 0 },
      uScroll: { value: 0 },
      uTime: { value: 0 },
    };
    const mat = new THREE.ShaderMaterial({
      uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const points = new THREE.Points(geo, mat);
    scene.add(points);

    gsap.to(uniforms.uAssembly, { value: 1, duration: 1.4, ease: "power3.out", delay: 0.35 });

    const st = ScrollTrigger.create({
      trigger: document.body,
      start: "top top",
      end: "+=750",
      scrub: 0.6,
      onUpdate: (self) => { uniforms.uScroll.value = self.progress * 0.7 / 0.7; },
    });

    const resize = () => {
      const w = mount.clientWidth || 1;
      const h = mount.clientHeight || 1;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      points.position.x = w > 1024 ? 2.6 : 0;
    };
    resize();
    window.addEventListener("resize", resize);

    let raf;
    const clock = new THREE.Clock();
    const loop = () => {
      uniforms.uTime.value = clock.getElapsedTime();
      points.rotation.y = Math.sin(clock.getElapsedTime() * 0.12) * 0.08;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      st.kill();
      window.removeEventListener("resize", resize);
      geo.dispose();
      mat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className={className} data-testid="particle-canvas" />;
};
